import React from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import FormattedContent from './FormattedContent';

export default function AnswerOption({
  questionId,
  answer,
  idx,
  selectedAnswerId,
  isValidated,
  onSelect,
  size = 'default'
}) {
  const letter = String.fromCharCode(65 + idx);
  const isChosen = selectedAnswerId === answer.id;
  const isCorrect = answer.is_correct === true;
  const isLarge = size === 'lg';

  // After validation, highlight both the correct answer AND the selected answer
  const shouldHighlight = isValidated && (isCorrect || isChosen);

  // Show explanations for all answers after validation
  const showingExplanation = isValidated;

  // Before validation: just highlight the selected answer
  // After validation: show correct answer in green, wrong selected answer in red
  const border = isValidated
    ? (shouldHighlight
      ? (isCorrect
        ? 'border-[var(--answer-correct-badge)] ring-2 ring-[var(--answer-correct-bg)]'
        : 'border-[var(--answer-wrong-badge)] ring-2 ring-[var(--answer-wrong-bg)]')
      : 'border-primary-200')
    : (isChosen
      ? 'border-primary-500 ring-2 ring-primary-200'
      : 'border-primary-200');

  const base = `w-full text-left flex items-center gap-3 rounded-2xl px-4 py-4 transition-colors shadow-sm border ${
    isLarge ? 'text-base sm:text-lg' : ''
  }`;

  const colors = isValidated
    ? (shouldHighlight
      ? (isCorrect
        ? 'bg-[var(--answer-correct-bg)] text-[var(--answer-correct-text)]'
        : 'bg-[var(--answer-wrong-bg)] text-[var(--answer-wrong-text)]')
      : 'bg-primary-50 text-secondary-900 hover:bg-primary-100')
    : (isChosen
      ? 'bg-primary-100 text-secondary-900'
      : 'bg-primary-50 text-secondary-900 hover:bg-primary-100');

  const letterBadge = isValidated
    ? (shouldHighlight
      ? (isCorrect
        ? 'bg-[var(--answer-correct-badge)] text-white'
        : 'bg-[var(--answer-wrong-badge)] text-white')
      : 'bg-primary-200 text-secondary-800')
    : (isChosen
      ? 'bg-primary-500 text-white'
      : 'bg-primary-200 text-secondary-800');

  const explanationStyles = shouldHighlight
    ? (isCorrect
      ? 'rounded-md bg-[var(--answer-correct-bg)] text-[var(--answer-correct-text)]'
      : 'rounded-md bg-[var(--answer-wrong-bg)] text-[var(--answer-wrong-text)]')
    : 'text-secondary-900';

  const showExplanation = showingExplanation && answer.explanation;

  return (
    <div
      role="button"
      tabIndex={0}
      className={`${base} ${colors} ${border}`}
      onClick={() => onSelect(questionId, answer.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(questionId, answer.id);
        }
      }}
    >
      <div className="flex w-full items-start gap-3">
        <span className={`inline-flex shrink-0 items-center justify-center rounded-full font-semibold ${
          isLarge ? 'h-8 w-8 text-base' : 'h-7 w-7 text-sm'
        } ${letterBadge}`}>
          {letter}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <FormattedContent
              text={answer.text}
              className={`min-w-0 flex-1 font-medium ${isLarge ? 'text-base sm:text-lg' : ''}`}
            />
            {shouldHighlight && (
              isCorrect ? (
                <CheckCircle2 className="shrink-0 text-[var(--answer-correct-text)]" size={isLarge ? 20 : 18} />
              ) : (
                <XCircle className="shrink-0 text-[var(--answer-wrong-text)]" size={isLarge ? 20 : 18} />
              )
            )}
          </div>
          {showExplanation && (
            <FormattedContent
              text={answer.explanation}
              className={`mt-2 ${isLarge ? 'text-base' : 'text-sm'} ${explanationStyles}`}
            />
          )}
        </div>
      </div>
    </div>
  );
}
